import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Video, HelpCircle, FileText, StickyNote, Trash2, ArrowRight, PlayCircle, MapPin, Microscope, PenTool, Lightbulb, BookOpen, GraduationCap, Map, Layers, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { bookmarksAPI } from '@/api/bookmarks';
import { userNotesAPI } from '@/api/userNotes';
import BookmarkIcon from './BookmarkIcon';
import ActivityModal from './ActivityModal';
import NoteModal from './NoteModal';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkLatexSanitizer from '@/utils/remarkLatexSanitizer';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';

/**
 * BookmarksView
 * Displays a hierarchical view of bookmarks organized by Tabs.
 * 
 * Tabs:
 * - Learning Pack (Activities/Videos) -> Grouped by Section
 * - Short Notes (Textbook Highlights/Notes) -> Flat List
 * - Teacher Notes (Assigned Content) -> Flat List
 * - My Notes (User Created) -> Flat List
 */
export default function BookmarksView({ chapterId, chapterName, sections = [], onNavigate, chapterAnnotations = [], packState, backendCompletions = {}, onActivityComplete, selectedTab = 'revision_flow', onTabChange, refreshTrigger = 0, onBookmarksUpdated }) {
    const [bookmarks, setBookmarks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState({});
    const [activeFlowIndex, setActiveFlowIndex] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        console.log('🔄 BookmarksView refreshing due to trigger:', refreshTrigger);
        loadBookmarks();
    }, [chapterId, chapterName, sections, refreshTrigger]);

    const loadBookmarks = async () => {
        if (!chapterId) return;
        setLoading(true);
        try {
            const [bookmarksData, notesData] = await Promise.all([
                bookmarksAPI.getChapterBookmarks(chapterId),
                userNotesAPI.list({ chapter_id: chapterId })
            ]);

            // Normalize user notes to bookmark structure
            const rawNotes = Array.isArray(notesData) ? notesData : (notesData?.results || []);
            const noteBookmarks = rawNotes.map(note => ({
                id: `user_note_${note.id}`,
                bookmark_type: 'user_note',
                section_number: note.section_name || 'general',
                content_id: note.id,
                content_data: {
                    title: note.heading || `Note on Page ${note.page_number}`,
                    preview_text: note.content,
                    ...note
                },
                created_at: note.created_at
            }));

            const safeBookmarks = Array.isArray(bookmarksData) ? bookmarksData : [];
            const allItems = [...safeBookmarks, ...noteBookmarks];
            setBookmarks(allItems);

            // Auto-expand all sections initially for better UX
            const sectionMap = {};
            allItems.forEach(b => {
                const key = b.section_number || b.section_name || 'general';
                sectionMap[key] = true;
            });
            sectionMap['Teacher Notes'] = true;
            setExpandedSections(sectionMap);

        } catch (error) {
            toast.error('Failed to load bookmarks');
        } finally {
            setLoading(false);
            if (onBookmarksUpdated) onBookmarksUpdated();
        }
    };

    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Ordered Revision Path
    const revisionPath = useMemo(() => {
        // 1. Flatten all bookmarks
        let allItems = [...bookmarks];

        // 2. Enrich with sorting data
        // We try to extract a sortable 'section value' and 'order within section'
        const enriched = allItems.map(item => {
            const sectionStr = item.section_number || '9999'; // Default to end
            // Parse section number (e.g., "1.2" -> 1.2)
            // Handle cases like "1.2.1", "Introduction" etc.
            // Simple approach: try partial float, fallback to string compare
            const sectionVal = parseFloat(sectionStr) || 9999;

            // Prioritize: Learning Pack > Short Notes > Teacher Notes > User Notes
            let typePriority = 4;
            if (item.bookmark_type === 'learning_pack' || item.bookmark_type === 'activity') typePriority = 1;
            else if (item.bookmark_type === 'note' && item.section_number === 'Teacher Notes') typePriority = 3; // Teacher notes
            else if (item.bookmark_type === 'user_note') typePriority = 4;
            else typePriority = 2; // Short notes

            return {
                ...item,
                _sortSection: sectionVal,
                _sortString: sectionStr,
                _typePriority: typePriority
            };
        });

        // 3. Sort
        enriched.sort((a, b) => {
            // Primary: Section Number
            if (a._sortSection !== b._sortSection) {
                return a._sortSection - b._sortSection;
            }
            // Secondary: String compare for same numeric value (e.g. "1.1" vs "1.1.1")
            if (a._sortString !== b._sortString) {
                return a._sortString.localeCompare(b._sortString, undefined, { numeric: true });
            }
            // Tertiary: Type Priority
            return a._typePriority - b._typePriority;
        });

        return enriched;
    }, [bookmarks]);

    const categorizedBookmarks = useMemo(() => {
        const categories = {
            learning_pack: [],
            short_notes: [],
            teacher_notes: [],
            user_notes: []
        };

        bookmarks.forEach(b => {
            // My Notes
            if (b.bookmark_type === 'user_note') {
                categories.user_notes.push(b);
                return;
            }

            // Learning Pack (Videos, Activities)
            if (b.bookmark_type === 'activity' || b.bookmark_type === 'learning_pack') {
                categories.learning_pack.push(b);
                return;
            }

            // Teacher Notes
            // Identified by 'Teacher Notes' scope
            if (b.section_number === 'Teacher Notes' || (b.content_id && String(b.content_id).startsWith('teacher_note'))) {
                categories.teacher_notes.push(b);
                return;
            }

            // Short Notes (Default fallback)
            categories.short_notes.push(b);
        });

        return categories;
    }, [bookmarks]);

    const renderIcon = (bookmark) => {
        const type = bookmark.bookmark_type;

        if (type === 'activity' || type === 'learning_pack') {
            // Find specific activity type
            let activityType = null;
            if (bookmark.content_id) {
                const annotation = chapterAnnotations.find(a => String(a.id) === String(bookmark.content_id));
                if (annotation) {
                    activityType = annotation.type || annotation.annotation_type;
                }
            }
            // Fallback to content_data if available
            if (!activityType && bookmark.content_data) {
                activityType = bookmark.content_data.type || bookmark.content_data.annotation_type;
            }

            // Allow case-insensitive matching if needed, or normalized strings
            // But usually the types are PascalCase as defined in backend/mocks

            switch (activityType) {
                case 'Curiosity': return <Lightbulb size={16} className="text-yellow-500" />;
                case 'Explore': return <Microscope size={16} className="text-teal-500" />;
                case 'Practice': return <PenTool size={16} className="text-orange-500" />;
                case 'Watch': return <BookOpen size={16} className="text-blue-500" />;
                case 'BasicConcepts': return <GraduationCap size={16} className="text-indigo-500" />;
                case 'Video': return <Video size={16} className="text-pink-500" />;
                default:
                    // Fallback
                    return <HelpCircle size={16} className="text-purple-500" />;
            }
        }

        switch (type) {
            case 'text': return <FileText size={16} className="text-orange-500" />;
            case 'note': return <StickyNote size={16} className="text-yellow-500" />;
            case 'user_note': return <StickyNote size={16} className="text-green-500" />;
            default: return <FileText size={16} />;
        }
    };

    const getLabel = (bookmark) => {
        const data = bookmark.content_data || {};

        // For notes (Short Notes & Teacher's Notes), extract the term name
        if (data.preview_text && bookmark.bookmark_type === 'note') {
            // Try markdown format first: **Term**: definition
            let match = data.preview_text.match(/^\*\*([^*]+)\*\*:/);
            if (match && match[1]) {
                return match[1].trim();
            }

            // Try HTML format: <b>Term:</b> definition or <b>Term</b>: definition
            if (match && match[1]) {
                return match[1].trim();
            }
        }

        // 1. Try Live Annotation Data (for Learning Pack / Activities)
        // If we have live chapterAnnotations, look up the fresh data to fix stale bookmarks
        if ((bookmark.bookmark_type === 'activity' || bookmark.bookmark_type === 'learning_pack') && bookmark.content_id) {
            const annotation = chapterAnnotations.find(a => String(a.id) === String(bookmark.content_id));
            if (annotation) {
                if (annotation.one_liner) return annotation.one_liner;
                if (annotation.title) return annotation.title;

                // Fallback for Video/Watch activities which often have empty titles
                const type = annotation.type || annotation.annotation_type;
                if (type === 'Video') return 'Video Lesson';
                if (type === 'Watch') return 'Learn & Discover';
                if (type === 'Practice') return 'Quick Practice';
                if (type === 'Curiosity') return 'Curiosity Corner';
                if (type === 'Explore') return 'Explore Interactive';
                if (type === 'BasicConcepts') return 'Basic Concepts';
            }
        }

        // 2. Fallback to Stored Data
        // Prefer one_liner if available (especially for Learning Pack items)
        if (data.one_liner) return data.one_liner;
        if (data.title) return data.title;

        // Fallback for Stored Data if title is missing
        if (bookmark.bookmark_type === 'activity' || bookmark.bookmark_type === 'learning_pack') {
            // Try to find type in content_data
            const type = data.type || data.annotation_type;
            if (type === 'Video') return 'Video Lesson';
            if (type === 'Watch') return 'Learn & Discover';
            if (type === 'Practice') return 'Quick Practice';
            if (type === 'Curiosity') return 'Curiosity Corner';
            if (type === 'Explore') return 'Explore Interactive';
            if (type === 'BasicConcepts') return 'Basic Concepts';
        }

        if (bookmark.bookmark_type === 'text') return "Selected Text";
        return "Bookmarked Item";
    };

    const getPreview = (bookmark) => {
        const data = bookmark.content_data || {};
        let text = data.preview_text || data.content || null;

        if (!text) return null;

        // For notes (Short Notes & Teacher's Notes), clean formatting
        if (bookmark.bookmark_type === 'note') {
            // Remove the term prefix (both markdown and HTML formats)
            text = text
                .replace(/^\*\*[^*]+\*\*:\s*/, '')           // Remove **Term**: prefix
                .replace(/^<b>[^<]+<\/b>:?\s*/i, '');        // Remove <b>Term:</b> or <b>Term</b>: prefix

            // Strip HTML tags but KEEP LaTeX math delimiters
            text = text
                .replace(/<b>([^<]+)<\/b>/gi, '$1')          // Remove <b>text</b>
                .replace(/<i>([^<]+)<\/i>/gi, '$1')          // Remove <i>text</i>
                .replace(/<strong>([^<]+)<\/strong>/gi, '$1') // Remove <strong>text</strong>
                .replace(/<em>([^<]+)<\/em>/gi, '$1')        // Remove <em>text</em>
                .replace(/<\/?[^>]+(>|$)/g, '');             // Remove any remaining HTML tags

            // Strip markdown formatting but KEEP LaTeX math delimiters
            text = text
                .replace(/\*\*([^*]+)\*\*/g, '$1')  // Remove bold **text**
                .replace(/\*([^*]+)\*/g, '$1');      // Remove italic *text*
            // Keep $...$ and $$...$$ for LaTeX rendering
        }

        return text;
    };

    // Check if text contains LaTeX math
    const hasLatex = (text) => {
        return text && (text.includes('$') || text.includes('\\(') || text.includes('\\['));
    };

    // Handle bookmark click - open modal in flow context
    const handleBookmarkClick = (bookmark) => {
        // Find the index in the current revision path
        const index = revisionPath.findIndex(b => b.id === bookmark.id);

        if (index !== -1) {
            setActiveFlowIndex(index);
            setIsModalOpen(true);
        } else {
            // Fallback if not found in path
            if (onNavigate) {
                onNavigate(bookmark);
            }
        }
    };


    const renderBookmarkCard = (bookmark) => (
        <Card key={bookmark.id} className="group border shadow-sm hover:shadow-md transition-all bg-card/40 hover:bg-card/80 mb-3">
            <CardContent className="p-3 flex gap-3 items-start">
                <div className="mt-0.5 shrink-0 opacity-80">
                    {renderIcon(bookmark)}
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => handleBookmarkClick(bookmark)}>
                    <h4 className="text-sm font-medium text-foreground truncate pr-6 group-hover:text-primary transition-colors">
                        {getLabel(bookmark)}
                    </h4>
                    {getPreview(bookmark) && (
                        <div className="text-sm text-muted-foreground/90 line-clamp-3 mt-1 leading-relaxed">
                            {hasLatex(getPreview(bookmark)) ? (
                                <ReactMarkdown
                                    remarkPlugins={[remarkLatexSanitizer, remarkMath, remarkGfm]}
                                    rehypePlugins={[rehypeRaw, [rehypeKatex, { trust: true, strict: false }]]}
                                    components={{
                                        p: ({ node, ...props }) => <span {...props} />,
                                        em: ({ node, ...props }) => <em className="italic" {...props} />
                                    }}
                                >
                                    {getPreview(bookmark)}
                                </ReactMarkdown>
                            ) : (
                                <p className="font-serif">{getPreview(bookmark)}</p>
                            )}
                        </div>
                    )}
                    {(bookmark.section_number || bookmark.section_name) && bookmark.section_number !== 'General' && (
                        <div className="mt-2">
                            <span className="inline-flex items-center text-[10px] font-medium text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded border border-border/30">
                                {bookmark.section_number && <span className="font-mono mr-1.5">{bookmark.section_number}</span>}
                                {bookmark.section_name && <span className="truncate max-w-[150px] opacity-80">{bookmark.section_name}</span>}
                            </span>
                        </div>
                    )}
                </div>
                <div className="shrink-0 flex flex-col gap-1">
                    <BookmarkIcon
                        chapterId={chapterId}
                        initialBookmarked={true}
                        bookmarkId={bookmark.id}
                        onToggle={(isActive) => {
                            if (!isActive) {
                                console.log('🔄 BookmarksView: Bookmark removed from list');
                                loadBookmarks(); // Reload local list
                                // Notify parent to update global state
                                if (onBookmarksUpdated) {
                                    console.log('🔄 BookmarksView: Notifying parent onBookmarksUpdated');
                                    onBookmarksUpdated();
                                }
                            }
                        }}
                        size={16}
                        className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    />
                </div>
            </CardContent>
        </Card>
    );

    /**
     * Helper to render content, optionally grouped by section (Accordion)
     */
    const renderTabContent = (items, shouldGroup = false) => {
        if (!items || items.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                    <p>No bookmarks here yet.</p>
                </div>
            );
        }

        // FLAT LIST (Short Notes, Teacher Notes, My Notes)
        if (!shouldGroup) {
            return (
                <div className="space-y-1">
                    {items.map(item => renderBookmarkCard(item))}
                </div>
            );
        }

        // GROUPED LIST (Learning Pack)
        const groups = {};

        // Helper to get section number from a section object (supports both camelCase and snake_case)
        const getSectionNumber = (sec) => sec?.sectionNumber || sec?.section_number || '';
        const getSectionTitle = (sec) => sec?.title || sec?.section_name || sec?.heading || '';

        // Ensure known sections exist in order
        sections.forEach(sec => {
            const sn = getSectionNumber(sec);
            if (sn && items.some(i => i.section_number === sn)) {
                groups[sn] = [];
            }
        });

        items.forEach(item => {
            let key = item.section_number || 'General';
            if (!groups[key]) groups[key] = [];
            groups[key].push(item);
        });

        return (
            <div className="space-y-4">
                {Object.keys(groups).map(sectionKey => {
                    const sectionItems = groups[sectionKey];
                    if (!sectionItems || sectionItems.length === 0) return null;

                    let title = sectionKey;
                    // Find section info: Exact match first, then prefix match (parent check)
                    // Support both camelCase (API) and snake_case field names
                    const sectionInfo = sections.find(s => getSectionNumber(s) === sectionKey)
                        || sections.find(s => sectionKey.startsWith(getSectionNumber(s)));

                    if (sectionInfo) {
                        // Use the found section's title
                        const sectionTitle = getSectionTitle(sectionInfo);
                        // Construct nicely formatted title: "1.2 Section Title"
                        title = sectionTitle ? `${sectionKey}. ${sectionTitle}` : sectionKey;
                    }

                    const isExpanded = expandedSections[sectionKey] !== false;

                    return (
                        <div key={sectionKey} className="border-b border-border/40 last:border-0 pb-4">
                            <button
                                onClick={() => toggleSection(sectionKey)}
                                className="flex items-center w-full py-2 hover:bg-accent/50 rounded-md px-2 transition-colors text-left"
                            >
                                {isExpanded ? <ChevronDown size={16} className="mr-2 text-muted-foreground" /> : <ChevronRight size={16} className="mr-2 text-muted-foreground" />}
                                <span className="font-medium text-sm text-foreground">{title}</span>
                                <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{sectionItems.length}</span>
                            </button>

                            {isExpanded && (
                                <div className="mt-2 pl-4">
                                    {sectionItems.map(bookmark => renderBookmarkCard(bookmark))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    /**
     * Gamified Revision Map Renderer
     * Winding path with alternating nodes
     */
    const renderRevisionFlow = () => {
        if (!revisionPath || revisionPath.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
                    <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                        <MapPin className="w-8 h-8 opacity-40" />
                    </div>
                    <p className="text-lg font-medium mb-1">Your Revision Path is Empty</p>
                    <p className="text-sm max-w-[250px]">Bookmark items to build your revision map!</p>
                </div>
            );
        }

        const NODE_HEIGHT = 80;
        const GAP = 40;
        const TOTAL_HEIGHT = (revisionPath.length * (NODE_HEIGHT + GAP)) + 100;
        const MID_X = 150; // Center of the path container (assuming ~300px width)
        const AMPLITUDE = 60; // How wide the curve swings

        return (
            <div className="relative w-full min-h-[400px] bg-gradient-to-b from-blue-50/10 via-purple-50/10 to-orange-50/10 rounded-xl p-4">

                {/* SVG Winding Path */}
                <svg className="absolute top-0 left-0 w-full h-full pointer-events-none" style={{ height: TOTAL_HEIGHT }}>
                    <defs>
                        <linearGradient id="pathGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                            <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.6" />
                            <stop offset="100%" stopColor="#f97316" stopOpacity="0.4" />
                        </linearGradient>
                    </defs>
                    <motion.path
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                        d={`M ${MID_X} 20 
                           ${revisionPath.map((_, i) => {
                            const y = (i * (NODE_HEIGHT + GAP)) + 60;
                            const nextY = y + NODE_HEIGHT + GAP;
                            const isRight = i % 2 === 0;
                            const x = isRight ? MID_X + AMPLITUDE : MID_X - AMPLITUDE;
                            return `Q ${isRight ? MID_X + AMPLITUDE + 40 : MID_X - AMPLITUDE - 40} ${y + (NODE_HEIGHT / 2)}, ${MID_X} ${nextY}`;
                        }).join(' ')}`}
                        fill="none"
                        stroke="url(#pathGradient)"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeDasharray="10 6"
                    />
                </svg>

                {/* Start Flag */}
                <div className="absolute top-2 left-0 w-full flex justify-center z-10">
                    <div className="bg-green-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg border-2 border-white transform hover:scale-110 transition-transform">
                        START
                    </div>
                </div>

                {/* Nodes */}
                <div className="relative z-10 mt-12 pb-12" style={{ height: TOTAL_HEIGHT }}>
                    {revisionPath.map((item, index) => {
                        const isRight = index % 2 === 0;
                        const y = index * (NODE_HEIGHT + GAP);
                        // Positioning: We use simple flex rows, but control placement via margings or absolutes
                        // Actually absolute positioning is easier for the "Map" feel here

                        return (
                            <motion.div
                                key={item.id}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: index * 0.1, type: "spring" }}
                                style={{
                                    position: 'absolute',
                                    top: y,
                                    left: isRight ? '50%' : 'auto',
                                    right: isRight ? 'auto' : '50%',
                                    transform: isRight ? 'translateX(20px)' : 'translateX(-20px)',
                                    width: '45%' // Cards take up a bit less than half the width
                                }}
                                className={`flex ${isRight ? 'flex-row' : 'flex-row-reverse'} items-center gap-3`}
                            >
                                {/* The 'Dot' on the map */}
                                <div className={`w-4 h-4 rounded-full border-4 border-white shadow-md bg-gradient-to-br ${item.bookmark_type === 'learning_pack' ? 'from-purple-500 to-indigo-600' :
                                    item.bookmark_type === 'note' ? 'from-yellow-400 to-orange-500' :
                                        'from-blue-400 to-cyan-500'
                                    } shrink-0 absolute ${isRight ? '-left-[28px]' : '-right-[28px]'} top-1/2 -translate-y-1/2 z-20`} />

                                {/* The Content Card */}
                                <div
                                    onClick={() => handleBookmarkClick(item)}
                                    className={`relative bg-card/90 backdrop-blur-sm border-2 ${isRight ? 'rounded-tr-xl rounded-br-xl rounded-bl-xl' : 'rounded-tl-xl rounded-bl-xl rounded-br-xl'
                                        } p-2.5 shadow-sm hover:shadow-lg transition-all cursor-pointer hover:-translate-y-1 group w-full ${item.bookmark_type === 'learning_pack' ? 'border-purple-200 hover:border-purple-400' :
                                            item.bookmark_type === 'note' ? 'border-yellow-200 hover:border-yellow-400' :
                                                'border-blue-200 hover:border-blue-400'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-1 gap-2">
                                        <div className="p-1.5 rounded-lg bg-muted text-foreground/70">
                                            {renderIcon(item)}
                                        </div>
                                        <BookmarkIcon
                                            chapterId={chapterId}
                                            initialBookmarked={true}
                                            bookmarkId={item.id}
                                            onToggle={(isActive) => {
                                                if (!isActive) {
                                                    console.log('🔄 BookmarksView: RevisionFlow bookmark removed');
                                                    loadBookmarks();
                                                    if (onBookmarksUpdated) onBookmarksUpdated();
                                                }
                                            }}
                                            size={14}
                                            className="h-6 w-6 hover:bg-muted rounded-full"
                                        />
                                    </div>
                                    <h5 className="text-xs font-semibold leading-tight line-clamp-2 text-foreground mb-1">
                                        {getLabel(item)}
                                    </h5>
                                    <span className="inline-block text-[9px] font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                        Sec {item.section_number || '?'}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Finish Flag */}
                <div className="absolute bottom-4 left-0 w-full flex justify-center z-10" style={{ top: TOTAL_HEIGHT }}>
                    <div className="bg-foreground text-background text-[10px] font-bold px-4 py-1.5 rounded-full shadow-lg border-2 border-background/20 flex items-center gap-2 transform hover:scale-110 transition-transform">
                        <MapPin size={12} />
                        FINISH LINE
                    </div>
                </div>

            </div>
        );
    };

    // Header Logic - Moved before conditional return to fix Hook Order
    const displayHeader = useMemo(() => {
        if (!chapterName) return `Chapter ${chapterId}`;
        const normalized = chapterName.trim().toLowerCase();
        // Check if name already includes "Chapter {id}" to avoid duplication
        // Allowing for "Chapter 8" or "Chapter 08" variations if needed, but keeping it simple for now
        if (normalized.startsWith(`chapter ${chapterId}`) || normalized === `chapter ${chapterId}`) {
            return chapterName;
        }
        return (
            <>
                <span className="opacity-70 font-normal mr-2">Chapter {chapterId}</span>
                {chapterName}
            </>
        );
    }, [chapterId, chapterName]);

    if (loading) {
        return <div className="p-8 text-center text-muted-foreground">Loading bookmarks...</div>;
    }

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Main Header using Chapter Name, bright text */}
            <h3 className="text-xl font-bold mb-6 px-1 text-foreground leading-tight shrink-0">
                {displayHeader}
            </h3>

            <Tabs value={selectedTab} onValueChange={onTabChange} className="flex-1 flex flex-col w-full min-h-0">
                <TabsList className="w-full grid grid-cols-5 mb-4 bg-muted/50 h-auto p-1 shrink-0">
                    <TabsTrigger value="revision_flow" className="flex flex-col items-center gap-1.5 py-2 text-[10px] sm:text-xs px-0.5 shadow-none data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                        <Map size={16} className="opacity-70 data-[state=active]:opacity-100" />
                        <span className="truncate w-full text-center leading-none">Revision</span>
                    </TabsTrigger>
                    <TabsTrigger value="learning_pack" className="flex flex-col items-center gap-1.5 py-2 text-[10px] sm:text-xs px-0.5 shadow-none data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                        <Layers size={16} className="opacity-70 data-[state=active]:opacity-100" />
                        <span className="truncate w-full text-center leading-none">Learning Pack</span>
                    </TabsTrigger>
                    <TabsTrigger value="short_notes" className="flex flex-col items-center gap-1.5 py-2 text-[10px] sm:text-xs px-0.5 shadow-none data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                        <StickyNote size={16} className="opacity-70 data-[state=active]:opacity-100" />
                        <span className="truncate w-full text-center leading-none">Short Notes</span>
                    </TabsTrigger>
                    <TabsTrigger value="teacher_notes" className="flex flex-col items-center gap-1.5 py-2 text-[10px] sm:text-xs px-0.5 shadow-none data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                        <GraduationCap size={16} className="opacity-70 data-[state=active]:opacity-100" />
                        <span className="truncate w-full text-center leading-none">Teacher's Notes</span>
                    </TabsTrigger>
                    <TabsTrigger value="user_notes" className="flex flex-col items-center gap-1.5 py-2 text-[10px] sm:text-xs px-0.5 shadow-none data-[state=active]:bg-background data-[state=active]:text-primary data-[state=active]:shadow-sm transition-all">
                        <PenTool size={16} className="opacity-70 data-[state=active]:opacity-100" />
                        <span className="truncate w-full text-center leading-none">My Notes</span>
                    </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-y-auto pr-3 min-h-0 relative z-0">
                    <TabsContent value="revision_flow" className="mt-0 pb-10">
                        {renderRevisionFlow()}
                    </TabsContent>

                    <TabsContent value="learning_pack" className="mt-0">
                        {renderTabContent(categorizedBookmarks.learning_pack, true)}
                    </TabsContent>

                    <TabsContent value="short_notes" className="mt-0">
                        {renderTabContent(categorizedBookmarks.short_notes, false)}
                    </TabsContent>

                    <TabsContent value="teacher_notes" className="mt-0">
                        {renderTabContent(categorizedBookmarks.teacher_notes, false)}
                    </TabsContent>

                    <TabsContent value="user_notes" className="mt-0">
                        {renderTabContent(categorizedBookmarks.user_notes, false)}
                    </TabsContent>
                </div>
            </Tabs>

            {/* Activity Modal for Learning Pack items */}
            {isModalOpen && activeFlowIndex !== null && revisionPath[activeFlowIndex] && (() => {
                const activeItem = revisionPath[activeFlowIndex];
                const hasNext = activeFlowIndex < revisionPath.length - 1;
                const hasPrev = activeFlowIndex > 0;

                const handleNext = () => {
                    if (hasNext) setActiveFlowIndex(i => i + 1);
                };

                const handlePrev = () => {
                    if (hasPrev) setActiveFlowIndex(i => i - 1);
                };

                const handleClose = () => {
                    setIsModalOpen(false);
                };

                const isActivity = activeItem.bookmark_type === 'activity' || activeItem.bookmark_type === 'learning_pack';

                if (isActivity) {
                    // Transform for ActivityModal
                    let activityItem = activeItem;
                    // Try to find full annotation data if content_id links to it
                    if (activeItem.content_id) {
                        const annotation = chapterAnnotations.find(a => String(a.id) === String(activeItem.content_id));
                        if (annotation) {
                            activityItem = {
                                ...annotation,
                                type: annotation.type || annotation.annotation_type,
                                annotation_id: annotation.id,
                                section_name: activeItem.section_name,
                                section_number: activeItem.section_number
                            };
                        }
                    }

                    return (
                        <ActivityModal
                            item={activityItem}
                            isOpen={true}
                            onClose={handleClose}
                            onComplete={onActivityComplete || (() => { })}
                            isAlreadyCompleted={false}
                            chapterId={chapterId}
                            onNext={handleNext}
                            onPrev={handlePrev}
                            hasNext={hasNext}
                            hasPrev={hasPrev}
                            onBookmarkChange={() => loadBookmarks()}
                        />
                    );
                }

                return (
                    <NoteModal
                        item={activeItem}
                        isOpen={true}
                        onClose={handleClose}
                        onNext={handleNext}
                        onPrev={handlePrev}
                        hasNext={hasNext}
                        hasPrev={hasPrev}
                        chapterId={chapterId}
                    />
                );
            })()}
        </div>
    );
}
